import {
  formatCouponCodeForDetails,
  isCouponCodeTruncated,
} from '@shared/utils/couponCode';
import { parseToDayMonthFormat } from '@shared/utils/dateFormatters';
import { centsToDollar, centsToWholeDollar } from '@utils/currency';
import typography from '~styles/typography.scss';
import type { ReservationWithDetails } from './apiHelpers';
import styles from './ReceiptDetails.scss';

interface ReceiptDetailsProps {
  reservation: ReservationWithDetails;
}

export const ReceiptDetails = ({ reservation }: ReceiptDetailsProps) => {
  const { soldForPrice, transaction } = reservation;
  const couponCode = transaction.purchaseDiscount?.code ?? null;

  return (
    <section className={styles.container}>
      <h2 className={typography.h4} id="rd-h">
        Receipt
      </h2>
      <dl aria-labelledby="rd-h" className={typography.t1} role="group">
        <dt aria-labelledby="rd-pd-t" id="rd-pd-t">
          Purchase Date
        </dt>
        <dd aria-labelledby="rd-pd-d" id="rd-pd-d">
          {parseToDayMonthFormat(transaction.purchaseDate)}
        </dd>
        {transaction.transactionLineItems.map((lineItem, index) => (
          <div key={`${lineItem.name}-${index}`} className={styles.lineItem}>
            <dt aria-labelledby={`rd-li-t-${index}`} id={`rd-li-t-${index}`}>
              {lineItem.name} {lineItem.quantity > 1 && `x${lineItem.quantity}`}
            </dt>
            <dd aria-labelledby={`rd-li-d-${index}`} id={`rd-li-d-${index}`}>
              {centsToDollar(lineItem.pricePerUnit * lineItem.quantity)}
            </dd>
          </div>
        ))}
        {transaction.purchaseDiscount && (
          <div className={styles.lineItem}>
            <dt aria-labelledby="rd-di-t" id="rd-di-t">
              Coupon code
            </dt>
            <dd aria-labelledby="rd-di-d" id="rd-di-d">
              <span
                title={
                  isCouponCodeTruncated(couponCode)
                    ? (couponCode ?? undefined)
                    : undefined
                }
              >
                {formatCouponCodeForDetails(couponCode)}
              </span>
            </dd>
          </div>
        )}
        <dt aria-labelledby="rd-ta-t" id="rd-ta-t">
          Taxes
        </dt>
        <dd aria-labelledby="rd-ta-d" id="rd-ta-d">
          {centsToDollar(transaction.tax)}
        </dd>
        {transaction.fee !== 0 && (
          <>
            <dt aria-labelledby="rd-pf-t" id="rd-pf-t">
              Processing Fee
            </dt>
            <dd aria-labelledby="rd-pf-d" id="rd-pf-d">
              {centsToDollar(transaction.fee)}
            </dd>
          </>
        )}
        <dt aria-labelledby="rd-tp-t" id="rd-tp-t">
          Total Price
        </dt>
        <dd aria-labelledby="rd-tp-d" id="rd-tp-d">
          {centsToDollar(transaction.total)}
        </dd>
        {transaction.refundAmount && (
          <>
            <dt aria-labelledby="rd-ra-t" id="rd-ra-t">
              Refund Amount
            </dt>
            <dd aria-labelledby="rd-ra-d" id="rd-ra-d">
              {centsToDollar(transaction.refundAmount)}
            </dd>
          </>
        )}
        {soldForPrice && (
          <>
            <dt aria-labelledby="rd-sf-t" id="rd-sf-t">
              Sold For
            </dt>
            <dd aria-labelledby="rd-sf-d" id="rd-sf-d">
              {centsToWholeDollar(soldForPrice)}
            </dd>
          </>
        )}
        {transaction.paymentMethod && (
          <>
            <dt aria-labelledby="rd-pt-t" id="rd-pt-t">
              Payment Type
            </dt>
            <dd aria-labelledby="rd-pt-d" id="rd-pt-d">
              {transaction.paymentMethod}
            </dd>
          </>
        )}
        <dt aria-labelledby="rd-rn-t" id="rd-rn-t">
          Receipt Number
        </dt>
        <dd aria-labelledby="rd-rn-d" id="rd-rn-d">
          {transaction.receiptNumber}
        </dd>
      </dl>
    </section>
  );
};
